import {
  Button,
  ButtonGroup,
  Card,
  CardBody,
  Heading,
  Icon,
  Interactable,
  Paragraph,
  Separator,
  useMatchMediaTierProp,
} from "@george-labs.com/design-system"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router"
import { useOfferData } from "../../app/hooks"
import { useOfferScreenFlow } from "../../hooks/useOfferScreenFlow"
import { DataListComponent } from "../../Shared"
import OfferAccountInfo from "./OfferAccountInfo"
import {
  OfferActionButton,
  OfferNavButtons,
  OfferSaveButton,
} from "./OfferNavButtons"
import { OfferSignersComponent } from "./OfferSigners"
import { OFFER_STEPS } from "./OfferStepper"
import { useOfferHelperUtil } from "./useOfferHelperUtil"
import { ICON } from "@george-labs.com/george-assets/icons"

const OfferSummary = () => {
  const { t } = useTranslation()
  const offerHelper = useOfferHelperUtil()
  const navigate = useNavigate()
  const offerData = useOfferData()
  const { setNextStep } = useOfferScreenFlow()

  if (offerData?.offerState !== "SUMMARY") {
    return <></>
  }

  const loanCaseFieldArr = offerHelper.getPricingFields(
    offerData?.productInstances?.find((el) => !!el),
  )
  const customerFieldArr = offerHelper.getCustomerFields(offerData)
  const selectedAccount = offerData?.accounts?.find((el) => el.selected)

  const headerContent = <Heading level={1}>{t("offers.summary.title")}</Heading>

  const textContent = (
    <Paragraph style={{ marginBottom: "1rem" }}>
      {t("offers.summary.text")}
    </Paragraph>
  )

  const loanCaseHeaderContent = (
    <Heading level={3}>{t("offers.summary.loancase.title")}</Heading>
  )

  const customerHeaderContent = (
    <Heading level={3}>{t("offers.summary.customer.title")}</Heading>
  )

  const customerTextContent = (
    <Paragraph style={{ marginBottom: "1rem" }}>
      {t("offers.summary.customer.text")}
    </Paragraph>
  )

  const accountHeaderContent = (
    <Heading level={3}>{t("offers.summary.account.title")}</Heading>
  )

  const accountInfo = (
    <OfferAccountInfo
      accInfo={offerData?.accounts?.find((el) => el.selected)}
    />
  )

  const accountCardXs =
    offerData?.accounts?.length > 1 ? (
      <Interactable
        secondaryActions={
          <Button
            onClick={() =>
              navigate(`/offer/${OFFER_STEPS.Accounts}`, { replace: true })
            }
          >
            <Icon assetIcon={ICON.CARET_RIGHT} size={Icon.SIZE.SIZE_24} />
          </Button>
        }
      >
        {accountInfo}
      </Interactable>
    ) : (
      <Card>{accountInfo}</Card>
    )

  const accountContentXs = selectedAccount ? accountCardXs : <></>

  const accountCard = (
    <>
      <Card
        style={{
          border: "1px",
          borderRadius: "16px",
        }}
      >
        {accountInfo}
      </Card>
      {offerData?.accounts?.length > 1 && (
        <ButtonGroup className="text-center">
          <OfferActionButton
            actionButtonText={t("offers.summary.button.edit")}
            icon={Button.ICON.EDIT}
            onActionButtonClicked={() =>
              navigate(`/offer/${OFFER_STEPS.Accounts}`, { replace: true })
            }
            className="mt-3"
          />
        </ButtonGroup>
      )}
    </>
  )

  const signersHeaderContent = (
    <Heading level={3}>{t("offers.summary.signers.title")}</Heading>
  )

  const nextButtonHandler = () => {
    setNextStep(offerData?.caseId)
  }

  const navButtonsContent = (
    <OfferNavButtons onNextButtonClicked={nextButtonHandler} />
  )

  const saveButtonHandler = () => {
    navigate("/dashboard", { replace: true })
  }

  const saveButtonContent = (
    <OfferSaveButton onSaveButtonClicked={saveButtonHandler} />
  )

  const pageContent = useMatchMediaTierProp({
    xs: (
      <>
        {headerContent}

        {textContent}

        {loanCaseHeaderContent}

        <Card padding={Card.PADDING.NONE}>
          <DataListComponent
            dataList={loanCaseFieldArr}
            displayAsLabeledList={true}
          />
        </Card>

        {customerHeaderContent}
        {customerTextContent}

        <Card padding={Card.PADDING.NONE}>
          <DataListComponent
            dataList={customerFieldArr}
            displayAsLabeledList={true}
          />
        </Card>

        {accountHeaderContent}

        {accountContentXs}

        {signersHeaderContent}

        <OfferSignersComponent
          signers={offerData?.individuals}
          isCompact={true}
        />

        {navButtonsContent}
      </>
    ),
    md: (
      <Card nested={false}>
        <CardBody>
          {headerContent}

          {textContent}

          {loanCaseHeaderContent}

          <DataListComponent
            dataList={loanCaseFieldArr}
            displayAsLabeledList={true}
            isStripedLabeledList={true}
          />
        </CardBody>

        <Separator />

        <CardBody>
          {customerHeaderContent}
          {customerTextContent}

          <DataListComponent
            dataList={customerFieldArr}
            displayAsLabeledList={true}
            isStripedLabeledList={true}
          />
        </CardBody>

        <Separator />

        {selectedAccount && (
          <>
            <CardBody>
              {accountHeaderContent}

              {accountCard}
            </CardBody>
            <Separator />
          </>
        )}

        <CardBody>
          {signersHeaderContent}

          <OfferSignersComponent
            signers={offerData?.individuals}
            isCompact={true}
          />
        </CardBody>

        <Separator />

        <CardBody>{navButtonsContent}</CardBody>
      </Card>
    ),
  })

  return (
    <>
      {pageContent}

      {saveButtonContent}
    </>
  )
}

export default OfferSummary
